import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Shield, Handshake, FileCheck, Building2, HardHat, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';

export default function GarantiesPage() {
  const navigate = useNavigate();

  const ecosysteme = [
    {
      icon: "🤝",
      title: "COPPAM – Garanties Solidaires",
      description: "Accueil, pré-diagnostic, accompagnement, médiation sociale, montage des dossiers et interface entre professionnels et particuliers."
    },
    {
      icon: "📋",
      title: "F.O.G.E.D.O.M – Instruction",
      description: "Instruction sociale, territoriale et technique. Diagnostic, vérification et rapport d'instruction transmis au Fonds FOGEDOM-SCIC."
    },
    {
      icon: "🔎",
      title: "Fonds FOGEDOM-SCIC – Décision",
      description: "Instance souveraine de décision : RCD Solidaire, DOS, CAP BÂTI+, BATICO, DOMIUM..."
    },
    {
      icon: "🏛️",
      title: "SCIC O'SCOP – Gouvernance",
      description: "Gestion administrative, coordination, facturation, encaissement et supervision du dispositif coopératif multi-acteurs."
    }
  ];

  const garanties = [
    {
      title: "BATICO – RCD Solidaire",
      description: "Responsabilité Civile & Décennale Solidaire",
      reference: "(Art. 1792 – Code civil)",
      icon: Shield
    },
    {
      title: "NOMINATIVE – RCD Solidaire au chantier",
      description: "Accompagnement civil & solidaire pour un chantier identifié",
      reference: "(Art. 1792 – Code civil)",
      icon: FileCheck
    },
    {
      title: "DOMIUM – Aide & Prévention",
      description: "Mécanisme coopératif d'entraide destiné aux constructeurs, artisans, maîtres d'œuvre et maîtres d'ouvrage confrontés à des difficultés techniques ou civiles post-travaux.",
      reference: null,
      icon: Handshake
    },
    {
      title: "CAP BÂTI",
      description: "Dispositif coopératif de prévention, d'accompagnement et de contrôle technique pour le Constructeur de Maisons Individuelles",
      reference: "(Articles 1710 et suivants du Code civil + CCH L.230-1 à L.232-2)",
      icon: Home
    },
    {
      title: "CAP BÂTI+",
      description: "Sécurisation solidaire de livraison pour les maîtres d'ouvrage et constructeurs confrontés à des difficultés de livraison ou dépassement de prix, dans le respect du Code civil.",
      reference: null,
      icon: Building2
    },
    {
      title: "ARCHOS – RCDS",
      description: "Forme avancée de soutien civil solidaire destinée aux membres œuvrant dans les métiers du bâtiment, de l'ingénierie, de la maîtrise d'œuvre ou des opérations techniques à responsabilité élevée.",
      reference: null,
      icon: HardHat
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header avec image de fond */}
      <header className="relative bg-[#111] text-white overflow-hidden">
        {/* Image de fond */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1920&q=80')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#111]/90 to-[#111]/70" />
        
        <div className="relative z-10 py-20 px-6">
          <div className="max-w-5xl mx-auto text-center">
            <div className="mb-6 flex justify-center">
              <BackButton variant="dark" label="Retour" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              COPPAM – <span className="text-[#d4af37]">Garanties Solidaires</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Un dispositif coopératif innovant de sécurisation des chantiers, basé sur l'entraide, 
              la prévention, la solidarité et la gouvernance partagée.
            </p>
          </div>
        </div>
      </header>

      {/* Notre mission avec image */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#111] mb-6">Notre mission</h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 mb-4">
                  COPPAM – Garanties Solidaires accompagne les entreprises du bâtiment, les particuliers, 
                  les maîtres d'ouvrage et les porteurs de projets grâce à un mécanisme solidaire fondé sur 
                  la <strong>mutualisation</strong> et le <strong>droit civil</strong>.
                </p>
                <p className="text-gray-700">
                  À travers un écosystème structuré (COPPAM / F.O.G.E.D.O.M / FOGEDOM-SCIC / O'SCOP), nous renforçons la{' '}
                  <strong>sécurisation des chantiers</strong>, la <strong>gestion des dommages</strong>,{' '}
                  l'<strong>aide post-travaux</strong> et la <strong>prévention</strong>.
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <span className="px-4 py-2 bg-[#d4af37]/10 text-[#d4af37] rounded-full text-sm font-medium">
                  Mutualisation
                </span>
                <span className="px-4 py-2 bg-[#d4af37]/10 text-[#d4af37] rounded-full text-sm font-medium">
                  Droit civil
                </span>
                <span className="px-4 py-2 bg-[#d4af37]/10 text-[#d4af37] rounded-full text-sm font-medium">
                  Solidarité
                </span>
                <span className="px-4 py-2 bg-[#d4af37]/10 text-[#d4af37] rounded-full text-sm font-medium">
                  Prévention
                </span>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=600&q=80"
                alt="Équipe de construction travaillant ensemble"
                className="rounded-xl shadow-xl w-full h-80 object-cover"
              />
              <div className="absolute -bottom-4 -left-4 bg-[#d4af37] text-white px-6 py-3 rounded-lg shadow-lg">
                <p className="text-sm font-bold">Depuis 2015</p>
                <p className="text-xs">Au service des professionnels</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Notre écosystème solidaire */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#111] mb-4">Notre écosystème solidaire</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Une organisation structurée pour garantir un accompagnement complet et efficace
            </p>
          </div>
          
          {/* Image illustrative */}
          <div className="mb-12">
            <img 
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=1200&q=80"
              alt="Équipe professionnelle en réunion"
              className="w-full h-64 object-cover rounded-xl shadow-lg"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ecosysteme.map((item, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow border-t-4 border-t-[#d4af37] group">
                <CardContent className="pt-6">
                  <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">{item.icon}</div>
                  <h3 className="text-lg font-bold text-[#111] mb-3">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Nos garanties solidaires */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#111] mb-4">Nos garanties solidaires</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Des dispositifs adaptés à chaque besoin, fondés sur le Code civil et la solidarité coopérative
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {garanties.map((garantie, index) => {
              const Icon = garantie.icon;
              return (
                <Card key={index} className="hover:shadow-xl transition-all hover:-translate-y-1 bg-white overflow-hidden group">
                  <div className="h-2 bg-gradient-to-r from-[#d4af37] to-[#e8c55a]" />
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-[#d4af37]/10 rounded-lg group-hover:bg-[#d4af37]/20 transition-colors">
                        <Icon className="w-6 h-6 text-[#d4af37]" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-[#111] mb-2">{garantie.title}</h3>
                        <p className="text-gray-700 text-sm mb-2">{garantie.description}</p>
                        {garantie.reference && (
                          <p className="text-xs text-gray-500 italic">{garantie.reference}</p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section chiffres clés */}
      <section className="py-16 px-6 bg-[#111]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold text-[#d4af37]">500+</p>
              <p className="text-gray-400 text-sm mt-2">Entreprises accompagnées</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#d4af37]">10 ans</p>
              <p className="text-gray-400 text-sm mt-2">D'expérience</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#d4af37]">98%</p>
              <p className="text-gray-400 text-sm mt-2">Taux de satisfaction</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#d4af37]">6</p>
              <p className="text-gray-400 text-sm mt-2">Garanties disponibles</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action avec image */}
      <section className="py-16 px-6 relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#1e3a5f] to-[#2d5a87]" />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <img 
              src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=400&q=80"
              alt="Partenariat et confiance"
              className="w-32 h-32 object-cover rounded-full mx-auto border-4 border-white shadow-xl"
            />
          </div>
          <h2 className="text-3xl font-bold text-white mb-6">Besoin d'accompagnement ?</h2>
          <p className="text-gray-200 mb-8 max-w-2xl mx-auto">
            Notre équipe est à votre disposition pour étudier votre situation et vous orienter 
            vers la solution adaptée à vos besoins. Rejoignez notre réseau de professionnels solidaires.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-[#d4af37] hover:bg-[#c4a030] text-white px-4 md:px-8"
              onClick={() => navigate('/contact')}
            >
              Nous contacter
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white text-white hover:bg-white/10 px-4 md:px-8"
              onClick={() => navigate('/register')}
            >
              Devenir membre
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#111] text-white py-10 px-6 text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Shield className="w-6 h-6 text-[#d4af37]" />
          <span className="text-lg font-semibold">COPPAM – Garanties Solidaires</span>
        </div>
        <p className="text-gray-500 text-sm">
          Dispositif coopératif ESS • Économie Sociale et Solidaire
        </p>
      </footer>
    </div>
  );
}
